// src/components/production/EngravingDisplay.js
import React, { useState } from "react";
import { PenTool, Copy, Check, AlertTriangle, AlertCircle } from "lucide-react";
import { parseEngravingParts, copyToClipboard } from "../../utils/engravingUtils";

/**
 * Componente para exibição inteligente de gravações com suporte a múltiplas peças (Medalha 1/2, Frente/Verso),
 * contador dinâmico de caracteres com cores de alerta e botão de cópia rápida para o operador de laser.
 */
export default function EngravingDisplay({
  rawText,
  mode = "compact", // 'compact' | 'detailed'
  editable = false, // Permite editar os textos diretamente em cada sub-campo
  onUpdatePart = null, // Callback (partIndex, newText) => void
  showIcon = true,
  className = "",
}) {
  const [copiedId, setCopiedId] = useState(null);

  const parts = parseEngravingParts(rawText);

  if (!parts || parts.length === 0) {
    return null;
  }

  const handleCopy = async (id, text, e) => {
    if (e) e.stopPropagation();
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // --- MODO COMPACTO (Para tabelas, linhas de produção, cards de reserva e kanban) ---
  if (mode === "compact") {
    if (parts.length === 1) {
      const part = parts[0];
      return (
        <div
          className={`inline-flex items-center gap-1.5 text-xs text-purple-900 bg-purple-50/90 px-2 py-0.5 rounded border border-purple-100 shadow-2xs max-w-full ${className}`}
          title={`Gravação: "${part.text}" (${part.count} caracteres)`}
        >
          {showIcon && <PenTool size={10} className="text-purple-400 shrink-0" />}
          {part.label && (
            <span className="text-[10px] font-bold text-purple-600 uppercase shrink-0">
              {part.label}:
            </span>
          )}
          <span className="italic font-bold break-words leading-tight truncate">
            "{part.text}"
          </span>
          <span
            className={`text-[9px] px-1.5 py-0.2 rounded-full border shrink-0 transition-colors ${part.badgeClass}`}
          >
            {part.count} carac.
          </span>
        </div>
      );
    }

    // Múltiplas partes no modo compacto
    return (
      <div className={`flex flex-wrap items-center gap-1.5 max-w-full ${className}`}>
        {parts.map((part) => (
          <div
            key={part.id}
            className="inline-flex items-center gap-1 text-[11px] text-purple-900 bg-purple-50/90 px-1.5 py-0.5 rounded border border-purple-100 shadow-2xs max-w-full"
            title={`${part.label || "Parte"}: "${part.text}" (${part.count} caracteres)`}
          >
            {showIcon && <PenTool size={9} className="text-purple-400 shrink-0" />}
            <span className="font-extrabold text-[10px] text-purple-700 uppercase shrink-0">
              {part.label || "Item"}:
            </span>
            <span className="italic font-bold truncate max-w-[140px] md:max-w-[180px]">
              "{part.text}"
            </span>
            <span
              className={`text-[9px] px-1 py-0.2 rounded-full border shrink-0 ${part.badgeClass}`}
            >
              {part.count}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // --- MODO DETALHADO (Para QuickOrderViewModal, Formulários e Modais de Consulta) ---
  return (
    <div className={`space-y-2 w-full ${className}`}>
      {parts.map((part, idx) => {
        const isCopied = copiedId === part.id;
        return (
          <div
            key={part.id || idx}
            className="p-2.5 rounded-xl bg-white border border-purple-100 shadow-2xs hover:border-purple-300 transition-all"
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-1.5">
                {showIcon && <PenTool size={11} className="text-purple-500" />}
                <span className="text-[11px] font-extrabold text-purple-800 uppercase tracking-wider">
                  {part.label || `Gravação ${parts.length > 1 ? idx + 1 : ""}`}
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                {/* Badge de Contagem com Alerta */}
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 transition-colors ${part.badgeClass}`}
                >
                  {part.status === "danger" && (
                    <AlertCircle size={10} className="text-red-500 shrink-0" />
                  )}
                  {part.status === "warning" && (
                    <AlertTriangle size={10} className="text-amber-500 shrink-0" />
                  )}
                  <span>{part.count} carac.</span>
                </span>

                {/* Botão de Copiar Rápido para o Laser */}
                <button
                  type="button"
                  onClick={(e) => handleCopy(part.id, part.text, e)}
                  className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-lg border transition-all active:scale-95 cursor-pointer ${
                    isCopied
                      ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                      : "bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                  }`}
                  title="Copiar texto exato para colar no software de gravação a laser"
                >
                  {isCopied ? (
                    <>
                      <Check size={11} className="text-emerald-600" />
                      <span>Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={11} className="text-purple-600" />
                      <span>Copiar</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Texto: se editable = true, é um input real. Se false, é texto selecionável sem select-all forçado */}
            {editable && onUpdatePart ? (
              <div className="relative">
                <input
                  type="text"
                  value={part.text}
                  onChange={(e) => onUpdatePart(idx, e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-800 outline-none focus:border-purple-500 bg-slate-50/50 focus:bg-white transition-all"
                  placeholder={`Texto para ${part.label || "gravação"}...`}
                />
              </div>
            ) : (
              <p className="text-xs font-mono font-bold text-slate-800 bg-slate-50 p-2 rounded-lg border border-slate-100 break-words leading-relaxed">
                "{part.text}"
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
